/**
 * "Also in Ownership" — the tag the design puts on an ambassador and a lead
 * contact who is also on the share register.
 *
 * ## Admin-only, and that is the whole design of it
 *
 * Decision 13: the *fact* that somebody is a shareholder is as admin-only as the
 * register itself. A sales rep opening a lead should not learn from a tag that
 * the person they are about to email owns part of the company.
 *
 * So this renders nothing at all for a non-admin — not a greyed tag, not a
 * placeholder. A hidden thing that leaves a gap where it would have been is
 * still a disclosure.
 *
 * It also reads the register itself rather than a flag on the ambassador or the
 * lead. One source of truth: linking is done on the shareholder record, and a
 * copy on the other side is a copy that goes stale the first time a link
 * changes.
 */
import { useEffect, useState } from 'react'
import { useAuth } from '@/features/auth/AuthProvider'
import { subscribeToOwnership } from './repository/ownershipRepository'
import { holderStatus, netShares, type ShareTransfer, type Shareholder } from '@/types/ownership'
import './Ownership.css'

export function AlsoInOwnership({
  leadId,
  contactEmail,
  ambassadorId,
  teamEmail,
}: {
  /** The lead company — a corporate holder links here. */
  leadId?: string
  /** A named contact on it — a person links here, keyed on their address. */
  contactEmail?: string
  ambassadorId?: string
  teamEmail?: string
}) {
  const { isAdmin } = useAuth()
  const [holders, setHolders] = useState<Shareholder[]>([])
  const [transfers, setTransfers] = useState<ShareTransfer[]>([])

  useEffect(() => {
    // Not subscribed at all for a non-admin: the rules would refuse the read,
    // and asking for something you may not have is how a screen fills with
    // permission errors nobody can act on.
    if (!isAdmin) return
    return subscribeToOwnership(
      (d) => {
        setHolders(d.holders)
        setTransfers(d.transfers)
      },
      () => setHolders([]),
    )
  }, [isAdmin])

  if (!isAdmin) return null

  /**
   * Four ways to be on the register, and the tag now has three homes — decision
   * 48 adds the lead company itself.
   *
   * A corporate holder matches the lead; a person matches the contact, the
   * ambassador or the team member. The lead match is deliberately restricted to
   * holders that *are* companies, so a person linked to a contact does not make
   * their employer look like a shareholder.
   */
  const match = holders.find(
    (h) =>
      (leadId && h.kind === 'company' && h.linkedLeadId === leadId) ||
      (contactEmail && h.linkedLeadContactId === contactEmail.trim().toLowerCase()) ||
      (ambassadorId && h.linkedAmbassadorId === ambassadorId) ||
      (teamEmail && h.linkedTeamEmail === teamEmail.trim().toLowerCase()),
  )
  if (!match) return null

  const status = holderStatus(match, transfers)
  /**
   * Standing, not merely the fact of a link — decision 73.
   *
   * "Also in Ownership" on its own says somebody is on the register; it does
   * not say whether they hold anything today. A former holder and a current one
   * are different facts to walk into a meeting with, and the share count is the
   * one number that makes the difference concrete.
   */
  const held = netShares(match, transfers)
  const what =
    status === 'shareholder'
      ? `a shareholder, ${held.toLocaleString('nb-NO')} shares`
      : status === 'former'
        ? 'a former shareholder'
        : status === 'optionholder'
          ? 'an option holder'
          : 'a potential investor'

  return (
    <p className="alsoown">
      <span className="alsoown__tag">Also in Ownership</span>
      {match.name} is {what}. Admins only — see Internal → Ownership.
    </p>
  )
}
